import { ChatQuickActionStore } from "@core/base/chat-quick-action-store";
import { ChatSkillStore } from "@core/base/chat-skill-store";
import { IQuickActionUser } from "@common/microservice-types/db/quick-action/quick-action.user";
import { IUserSkill } from "@common/microservice-types/db/skill/skill.user";
import { ISessionInfo } from "@core/base/session-base.types";
import { OfficeChatRepository } from "@core/office/office-chat-repository/office-chat-repository";
import { IChatRepositorySetting } from "@core/office/office-chat-repository/office-chat-repository.types";
import { OfficeChatSettingBase } from "@core/office/office-chat-setting/office-chat-setting-base";
import { OfficeService } from "@core/office/office-service/office-service";
import { OfficeSessionService } from "@core/office/office-session/office.session.service";
import Office from "@core/shared/office.instance";
import type { IChatSetting } from '@core/types/chat-setting.types';
import { AiModelsService } from "@services/ai-models/ai-models.service";
import { QuickActionsService } from "@services/quick-actions/quick-actions.service";
import { BehaviorSubject, map, Observable, Subscription } from "rxjs";
import { OfficeChatSettingV1 } from "./office-chat-setting-v1";
import { IOfficeChatSetting } from "./office-chat-setting-v1.types";
import { UserSettingsApiService } from "@services/user-settings-api/user-settings-api.service";
import { InstructionsService } from "@services/instructions/instructions.service";

export class OfficeChatSettingAdapterV1 extends OfficeChatSettingV1 implements OfficeChatSettingBase
{
    private officeService = this.office;
    private sessionService = this.session;
    private quickActionStore = new ChatQuickActionStore(
        (actions) => this.persistQuickActions(actions),
        this.office.getHost().getTag(),
    );
    private webSearch$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private skillStore = new ChatSkillStore(
        (skills) => this.persistSkills(skills),
    );
    private imageModel$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private copilotId$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private readonly subscriptions = new Subscription();
    private sessionRevision = 0;
    private accountId: string | null | undefined;
    private repositoryReady = false;

    constructor(
        private office: OfficeService,
        private session: OfficeSessionService,
        private chatRepository: OfficeChatRepository,
        private aiModels: AiModelsService,
        private quickActionsService: QuickActionsService,
        private userSettingsApi: UserSettingsApiService,
        private instructions: InstructionsService,
        officeRuntime = Office,
    )
    {
        super(officeRuntime);
        this.subscriptions.add(this.instructions.confirmedInstruction$.subscribe((instruction) =>
        {
            if (instruction?.trim()) this.setSetting({ systemMessage: instruction });
            else super.set({ ...super.get(), role_information: '' });
        }));
        this.subscriptions.add(this.userSettingsApi.settings$.subscribe(settings =>
        {
            if (settings) this.webSearch$.next(!!settings.webSearchEnabled);
        }));
        this.defineHandlerForSessionChange();
    }

    public $getSetting(): Observable<IChatSetting>
    {
        const officeSetting = this.$setting;
        return officeSetting.pipe(map(value =>
        {
            return this.transformChatSetting(value);
        }));
    }

    public getSetting(): IChatSetting
    {
        const setting = super.get();
        return this.transformChatSetting(setting);
    }

    public setSetting(setting: IChatSetting): IChatSetting
    {
        const chatSetting = this.transformChatSetting(super.get());
        const newSetting = {
            ...chatSetting,
            ...setting,
            options: {
                ...chatSetting.options || {},
                ...setting.options || {}
            }
        };

        const officeSetting = this.transformOfficeSetting(newSetting);

        if (!officeSetting.model)
        {
            officeSetting.model = this.aiModels.getDefaultChatModel();
        }

        this.saveSettingToRepository(newSetting);
        super.set(officeSetting);
        return newSetting;
    }

    public getSettingOriginal<T>(): T
    {
        return super.get() as T;
    }

    public getQuickActionsDefault(): IQuickActionUser[]
    {
        return this.quickActionStore.getStoredActions();
    }

    public $getQuickActions(): Observable<IQuickActionUser[]>
    {
        return this.quickActionStore.$getQuickActions();
    }

    public setQuickActions(newActions: IQuickActionUser[]): IQuickActionUser[]
    {
        return this.quickActionStore.setQuickActions(newActions);
    }

    public $getSkills(): Observable<IUserSkill[]>
    {
        return this.skillStore.$getSkills();
    }

    public setSkills(newSkills: IUserSkill[]): IUserSkill[]
    {
        return this.skillStore.setSkills(newSkills);
    }

    public isDefaultSkill(skill: IUserSkill | null): boolean
    {
        return this.skillStore.isDefaultSkill(skill);
    }

    public setWebSearch(value: boolean): void
    {
        this.webSearch$.next(value);
    }

    public getWebSearch(): boolean
    {
        return this.webSearch$.getValue();
    }

    public $getWebSearch(): Observable<boolean>
    {
        return this.webSearch$.asObservable();
    }

    public setImageModel(apiName: string): void
    {
        this.imageModel$.next(apiName);
        const chatSetting = this.transformChatSetting(super.get());
        this.saveSettingToRepository(chatSetting);
    }

    public getImageModel(): string | null
    {
        return this.imageModel$.getValue();
    }

    public $getImageModel(): Observable<string | null>
    {
        return this.imageModel$.asObservable();
    }

    public setCopilotId(id: string): void
    {
        this.copilotId$.next(id);
        const chatSetting = this.transformChatSetting(super.get());
        this.saveSettingToRepository(chatSetting);
    }

    public getCopilotId(): string | null
    {
        return this.copilotId$.getValue();
    }

    public $getCopilotId(): Observable<string | null>
    {
        return this.copilotId$.asObservable();
    }

    public onDestroy(): void { this.sessionRevision++; this.subscriptions.unsubscribe(); }

    private transformChatSetting(officeSetting: IOfficeChatSetting): IChatSetting
    {
        return {
            systemMessage: officeSetting.role_information,
            options: {
                model: officeSetting.model,
                answerLengthOutputLimit: officeSetting.answer_length_output_limit,
                answerLengthInstruction: officeSetting.answer_length_instruction,
            }
        };
    }

    private transformOfficeSetting(chatSetting: IChatSetting): IOfficeChatSetting
    {
        const originalSetting = super.get();
        const setting = { ...originalSetting };

        if (chatSetting.systemMessage !== undefined)
        {
            setting.role_information = chatSetting.systemMessage;
        }

        if (chatSetting.options?.model)
        {
            setting.model = chatSetting.options.model;
        }

        if (chatSetting.options?.answerLengthOutputLimit !== undefined)
        {
            setting.answer_length_output_limit = chatSetting.options.answerLengthOutputLimit;
        }

        if (chatSetting.options?.answerLengthInstruction !== undefined)
        {
            setting.answer_length_instruction = chatSetting.options.answerLengthInstruction;
        }

        return setting;
    }

    private defineHandlerForSessionChange(): void
    {
        this.subscriptions.add(this.sessionService.sessionInfo$.subscribe(
            this.handlerForSessionChange.bind(this)
        ));
    }

    private async handlerForSessionChange(info: ISessionInfo | null): Promise<void>
    {
        const account = info?.user_id ? String(info.user_id) : null;
        if (account === this.accountId) return;
        this.accountId = account;
        const revision = ++this.sessionRevision;
        this.repositoryReady = false;
        super.set({ ...super.get(), role_information: '', model: '', answer_length_instruction: undefined, answer_length_output_limit: undefined });
        this.quickActionStore.setStoredActions([], false);
        this.skillStore.setStoredSkills([], false);
        this.webSearch$.next(false);
        this.imageModel$.next(null);
        this.copilotId$.next(null);
        this.instructions.setAccount(account);
        if (info && info.user_id)
        {
            this.chatRepository.defineSettingKey(`${ info.user_id }-${ this.officeRuntime.context.host }`);
            const setting = await this.chatRepository.readSetting();
            if (revision !== this.sessionRevision) return;
            this.repositoryReady = true;

            if (setting)
            {
                this.quickActionStore.setStoredActions(setting.quickActions ?? [], false);
                this.skillStore.setStoredSkills(setting.skills ?? [], false);
                this.imageModel$.next(setting.imageModel ?? null);
                this.copilotId$.next(setting.copilotId ?? null);
                const newSetting = this.getSettingFromRepositorySetting(setting);
                this.setSetting(newSetting);
            }
            else
            {
                const defaultChatModel = this.aiModels.getDefaultChatModel();
                if (defaultChatModel)
                {
                    this.setSetting({ options: { model: defaultChatModel } });
                }

                const defaultImageModel = this.aiModels.getDefaultModelForFeature('TEXT_TO_IMAGE');
                if (defaultImageModel)
                {
                    this.setImageModel(defaultImageModel.apiName);
                }
            }

            await this.instructions.initialize(setting?.systemMessage);
            if (revision !== this.sessionRevision) return;
            await this.applyApiModelSelections(revision);
        }
    }

    private async applyApiModelSelections(revision: number): Promise<void>
    {
        let apiSettings;
        try
        {
            apiSettings = await this.userSettingsApi.getSettings();
        }
        catch
        {
            return;
        }

        if (!apiSettings || revision !== this.sessionRevision) { return; }
        this.webSearch$.next(!!apiSettings.webSearchEnabled);

        const optionsPatch: IChatSetting['options'] = {};

        const chatModel = apiSettings.resolvedChatModel ?? apiSettings.selectedChatModel;
        if (chatModel?.apiName)
        {
            optionsPatch.model = chatModel.apiName;
        }

        if (apiSettings.answerLength?.outputTokenLimit)
        {
            optionsPatch.answerLengthOutputLimit = apiSettings.answerLength.outputTokenLimit;
        }

        if (apiSettings.answerLength?.instruction)
        {
            optionsPatch.answerLengthInstruction = apiSettings.answerLength.instruction;
        }

        optionsPatch.messageHistoryContextCount = apiSettings.messageHistoryId === null
            ? 0
            : apiSettings.messageHistory?.messageContextCount ?? 0;

        if (Object.keys(optionsPatch).length > 0)
        {
            this.setSetting({ options: optionsPatch });
        }

        if (apiSettings.selectedImageModel?.apiName)
        {
            this.imageModel$.next(apiSettings.selectedImageModel.apiName);
            const chatSetting = this.transformChatSetting(super.get());
            this.saveSettingToRepository(chatSetting);
        }
    }

    private persistSkills(skills: IUserSkill[]): void
    {
        if (!this.isRepositoryReady()) { return; }

        const chatSetting = this.transformChatSetting(super.get());
        this.saveSettingToRepository(chatSetting, this.quickActionStore.getStoredActions(), skills);
    }

    private persistQuickActions(actions: IQuickActionUser[]): void
    {
        if (!this.isRepositoryReady()) { return; }

        const chatSetting = this.transformChatSetting(super.get());
        this.saveSettingToRepository(chatSetting, actions);
    }

    private isRepositoryReady(): boolean
    {
        if (!this.repositoryReady) return false;
        try
        {
            this.chatRepository.getSettingKey();
            return true;
        }
        catch
        {
            return false;
        }
    }

    private getSettingFromRepositorySetting(repoSetting: IChatRepositorySetting): IChatSetting
    {
        return {
            systemMessage: repoSetting.systemMessage,
            options: repoSetting.options
        };
    }

    private saveSettingToRepository(setting: IChatSetting, quickActions = this.quickActionStore.getStoredActions(), skills = this.skillStore.getStoredSkills()): void
    {
        if (!this.isRepositoryReady()) { return; }

        const repositorySetting = {
            ...setting,
            quickActions,
            skills,
            imageModel: this.imageModel$.getValue() ?? undefined,
            copilotId: this.copilotId$.getValue() ?? undefined,
        };
        this.chatRepository.writeSetting(repositorySetting);
    }
}
